import { Dialog, DialogTrigger, DialogContent, DialogClose } from '@/components/atoms/Dialog';
import { Button } from '@/components/atoms/PcButton';
import { useState } from 'react';
import { Check } from 'lucide-react';
import { cn } from '@/utils/tailwind';
import { CPLAT_API_ROUTES } from '@/api';
import { useAppSelector } from '@/redux/store';
import { usePost } from '@/api';
import { SELLERBOX_PLUS_SUBSCRIPTION_NO } from '@/constants/subscription';

const CancelMembership = () => {
  const [selected, setSelected] = useState<string>('');
  const { CplatToken } = useAppSelector((state) => state.user);
  const subscriptionNo = SELLERBOX_PLUS_SUBSCRIPTION_NO;
  const [isComplete, setIsComplete] = useState(false);

  // 구독 해지 API
  const { mutate: setUnsubscription, isPending: isLoading } = usePost(
    CPLAT_API_ROUTES.setUnscription,
  );

  const handleUnsubscribe = () => {
    setUnsubscription(
      {
        cplatToken: CplatToken,
        subscriptionNo,
      },
      {
        onSuccess: (res) => {
          if (res.data.code === '200') {
            setIsComplete(true); // 해지 완료 상태로 전환
          } else {
            alert(res.data.message || '해지에 실패했습니다. 다시 시도해 주세요.');
          }
        },
        onError: () => {
          alert('해지에 실패했습니다. 다시 시도해 주세요.');
        },
      },
    );
  };

  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button
          variant="outline"
          className="h-[34px] rounded-sm border-gr-200 px-2.5 py-1.5 text-gr-700 shadow-none Body5M14"
        >
          멤버십 해지
        </Button>
      </DialogTrigger>
      <DialogContent noCloseBtn className="w-full max-w-[320px] p-[30px]">
        {isComplete ? (
          <section className="flex h-[378px] flex-col items-start justify-between">
            <div className="flex flex-col gap-1">
              <h2 className=" text-gr-900 Title3S18">멤버십 해지가 완료되었습니다</h2>
              <div className="text-left text-gr-700 Body5M14">
                그동안 셀러박스 멤버십을 이용해 주셔서 감사합니다.
                <br />
                더 발전하는 셀러박스가 되겠습니다.
                <br />
                셀러박스 멤버십은 언제든지 다시 이용이 가능합니다.
              </div>
            </div>
            <DialogClose asChild>
              <Button variant="muted" className="h-[44px] w-full rounded-[10px] Body1S16">
                확인
              </Button>
            </DialogClose>
          </section>
        ) : (
          <div className="flex h-[378px] flex-col items-start justify-between">
            <section className="flex  flex-col gap-[4px]">
              <h2 className="text-gr-900 Title3S18">멤버십을 해지하시겠어요?</h2>
              <h5 className="text-gr-700 Body5M14">
                더 나은 서비스 제공을 위해서 <br />
                셀러박스 멤버십을 해지하신 이유를 알려주세요
              </h5>
            </section>
            <DisappointingReasonList selected={selected} onSelect={setSelected} />
            <div className="mt-[20px] flex w-full gap-3">
              <Button
                variant="muted"
                className="h-[44px] flex-1 rounded-[16px] Body1S16 disabled:bg-gr-200"
                disabled={!selected || isLoading}
                onClick={handleUnsubscribe}
              >
                해지하기
              </Button>
              <DialogClose asChild>
                <Button
                  className="h-[44px] flex-1 rounded-[16px] Body1S16 disabled:bg-gr-200"
                  disabled={isLoading}
                >
                  계속 이용
                </Button>
              </DialogClose>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default CancelMembership;

type Props = {
  selected: string;
  onSelect: (id: string) => void;
};

const DisappointingReasonList = ({ selected, onSelect }: Props) => {
  return (
    <div className="flex w-full flex-col items-start gap-[12px]">
      {DISAPPOINTING_NEWS_OPTIONS.map(({ id, description }) => {
        const isSelected = selected === id;
        return (
          <button
            key={id}
            onClick={() => onSelect(isSelected ? '' : id)}
            className={cn(
              'flex h-[24px] w-full items-center justify-between text-[15px] font-medium leading-[22px] transition-colors duration-500',
              isSelected ? 'text-point-500' : 'text-gr-700',
            )}
          >
            <div>{description}</div>
            <Check
              className="h-[24px] w-[24px] text-point-500 transition-all duration-300"
              style={{ opacity: isSelected ? 1 : 0 }}
            />
          </button>
        );
      })}
    </div>
  );
};

export type FeedbackOption = {
  id: string;
  description: string;
};

export const DISAPPOINTING_NEWS_OPTIONS: FeedbackOption[] = [
  {
    id: 'disappointing_cancel_membership_content',
    description: '서비스 컨텐츠가 부족해서',
  },
  {
    id: 'disappointing_cancel_membership_correct',
    description: '제공 정보가 부정확해서',
  },
  {
    id: 'disappointing_cancel_membership_market',
    description: '연동 가능한 마켓이 없어서',
  },
  {
    id: 'disappointing_cancel_membership_hard',
    description: '서비스 이용이 어렵고 불편해서',
  },
  {
    id: 'disappinting_cancel_membership_other',
    description: '기타',
  },
];
